import { useEffect, useState } from "react";
import DemoShowcase from "../demo/DemoShowcase";
import { trackEvent } from "../../lib/analytics";

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

const FORMAT_LABELS = {
  fixed_kits: "Fixed kits",
  mix_match: "Mix and match",
  byob: "Build-your-own bundle",
  upsells: "Upsell layer",
};

const STATUS_CHIPS = {
  missing: { label: "Missing", icon: "✕", classes: "bg-blush text-ink" },
  partial: { label: "Partial", icon: "~", classes: "bg-sand text-ink" },
  present: { label: "Live", icon: "✓", classes: "bg-mint text-ink" },
};

/* The seasonal skin on the readiness report, keyed by the server's
   `bfcm.seasonKey` (the Bfcm model owns the calendar): the BFCM runway,
   the Christmas gift window, then the "build for 2027" year. Static on
   purpose: the tips layer is our own playbook, not per-store AI. */
const SEASONS = {
  bfcm: {
    leakKicker: "Where BFCM orders leak",
    leakHeadline: "BFCM shoppers buy in multiples. Here's your coverage.",
    tipLabel: "BFCM angle: ",
    tips: {
      fixed_kits: "Gift sets are the easiest BFCM yes: one price, one add to cart, zero thinking for the shopper.",
      mix_match: "Mix and match turns one discount into a bigger basket: shoppers add more to hit the deal.",
      byob: "Build-your-own boxes are the gifting format: one builder covers everyone on a shopper's list.",
      upsells: "Peak traffic with no upsell layer means your biggest audience of the year buys exactly one thing.",
    },
    playbookKicker: "The BFCM bundle playbook",
    playbook: [
      { title: "Launch by early October", body: "Bundles need live sales data to tune contents and pricing. Launch early and your BFCM ads point at offers already proven." },
      { title: "Anchor sets to your bestseller", body: "Your highest-traffic product page is your best bundle billboard. Build the first gift set around the product people already come for." },
      { title: "Ladder your price points", body: "Gift shoppers arrive with a budget in mind. A good, better, best spread gives $25, $50 and $100 each somewhere to land." },
      { title: "Show the savings", body: "A compare-at price on the bundle does the discount math for the shopper. Visible savings is what makes a set read as a deal." },
      { title: "Sync inventory to components", body: "Bundles that oversell components become the January refund pile. Component-level tracking is why bundle sellers see 80% fewer fulfillment issues." },
      { title: "Give bundles a home", body: "A dedicated gift or bundles collection gives every ad, email and influencer link one obvious place to land." },
    ],
    readyKicker: "Your BFCM bundles are ready",
    lineupNoun: "BFCM bundle lineup",
    gateBullet: "Six BFCM-ready bundles, built from your real products",
    unlockCta: "Show me my BFCM bundles",
    teamSetupBody: "Leave us your email and our team will work with you directly to get these bundles live before BFCM.",
    ctaKicker: "Early bird beats November",
    ctaHeadline: (storeName) => `These could be selling on ${storeName} well before Black Friday.`,
  },
  christmas: {
    leakKicker: "Where gift orders leak",
    leakHeadline: "Gift shoppers buy for a list. Here's your coverage.",
    tipLabel: "Gift angle: ",
    tips: {
      fixed_kits: "A named gift set answers December's hardest question: what to get. One price, wrapped and done.",
      mix_match: "Mix and match lets one discount finish a list: shoppers add one more to hit the deal.",
      byob: "Build-your-own boxes cover everyone still on the list: one builder, every giftee.",
      upsells: "Gift traffic with no upsell layer means every shopper checks out with exactly one present.",
    },
    playbookKicker: "The gift bundle playbook",
    playbook: [
      { title: "Beat the shipping cutoff", body: "Gift sets sell right up to your last ship date. Put the cutoff on the product page and every set becomes a now-or-never buy." },
      { title: "Anchor sets to your bestseller", body: "Your highest-traffic product page is your best bundle billboard. Build the first gift set around the product people already come for." },
      { title: "Ladder your price points", body: "Gift shoppers arrive with a budget in mind. A good, better, best spread gives $25, $50 and $100 each somewhere to land." },
      { title: "Show the savings", body: "A compare-at price on the bundle does the discount math for the shopper. Visible savings is what makes a set read as a deal." },
      { title: "Sync inventory to components", body: "Bundles that oversell components become the January refund pile. Component-level tracking is why bundle sellers see 80% fewer fulfillment issues." },
      { title: "Add a digital safety net", body: "Gift cards and digital add-ons keep selling after the cutoff. Pair them with sold-out sets and late shoppers still convert." },
    ],
    readyKicker: "Your gift bundles are ready",
    lineupNoun: "gift bundle lineup",
    gateBullet: "Six gift-ready bundles, built from your real products",
    unlockCta: "Show me my gift bundles",
    teamSetupBody: "Leave us your email and our team will work with you directly to get these bundles live before the shipping cutoff.",
    ctaKicker: "The gift window is open",
    ctaHeadline: (storeName) => `These could be selling on ${storeName} before the shipping cutoff.`,
  },
  newYear: {
    leakKicker: "Where 2027 orders leak",
    leakHeadline: "Shoppers buy in multiples all year. Here's your coverage.",
    tipLabel: "2027 angle: ",
    tips: {
      fixed_kits: "Fixed kits are the cheapest test you can run: one price, one add to cart, instant signal on what sells together.",
      mix_match: "Mix and match turns one discount into a bigger basket, in January as much as November.",
      byob: "Build-your-own boxes show which combinations shoppers actually want. That's 2027's product roadmap, free.",
      upsells: "A quiet quarter with no upsell layer means every session buys exactly one thing. The leak doesn't take holidays.",
    },
    playbookKicker: "The 2027 bundle playbook",
    playbook: [
      { title: "Test in the quiet quarter", body: "January traffic is low stakes. Every bundle you test now walks into the year's first peak as a proven offer." },
      { title: "Bundle the leftovers", body: "Post-holiday stock finds a second life in sets. Pair slow movers with bestsellers and clear shelves without storewide discounts." },
      { title: "Anchor sets to your bestseller", body: "Your highest-traffic product page is your best bundle billboard. Build the first set around the product people already come for." },
      { title: "Ladder your price points", body: "Shoppers arrive with a budget in mind. A good, better, best spread gives $25, $50 and $100 each somewhere to land." },
      { title: "Sync inventory to components", body: "Bundles that oversell components become the refund pile. Component-level tracking is why bundle sellers see 80% fewer fulfillment issues." },
      { title: "Start the Q4 clock now", body: "The stores that win next BFCM arrive with a year of bundle data. Every month of sales you bank now is tuning you won't rush in October." },
    ],
    readyKicker: "Your 2027 bundles are ready",
    lineupNoun: "2027 bundle lineup",
    gateBullet: "Six ready-to-test bundles, built from your real products",
    unlockCta: "Show me my 2027 bundles",
    teamSetupBody: "Leave us your email and our team will work with you directly to get these bundles live while the quarter is still quiet.",
    ctaKicker: "January beats October",
    ctaHeadline: (storeName) => `These could be earning on ${storeName} all year.`,
  },
};

const seasonSkin = (bfcm) => SEASONS[bfcm?.seasonKey] || SEASONS.bfcm;

const money = (currency, value) => `${currency}${Number(value ?? 0).toFixed(2)}`;

/* "$4.95 to $6.20", collapsing to a single figure when the ends meet. */
const moneyRange = (currency, low, high) =>
  Number(low) === Number(high) ? money(currency, low) : `${money(currency, low)} to ${money(currency, high)}`;

/* The audit report: score and pain for free (score, gap, missing formats),
   then the six bundles. On the gated door (the AOV audit) the bundles hide
   behind the email gate: the locked half is never in the DOM until the
   server hands it over at unlock, and the gate's blurred stand-in sits
   last, reading as "the rest of the report". On the ungated door (BFCM
   readiness, `teaser.ungated`) the bundles render for everyone and the
   email asks become two optional CTAs instead: mail-me-my-results right
   before the bundles, let-our-team-set-this-up right after. When the
   teaser carries a `bfcm` payload, the page wears its seasonal skin:
   countdown, readiness band, tips and the playbook. */
export default function AovAuditReport({ teaser, report: initialReport, claimUrl, supportUrl }) {
  const [report, setReport] = useState(initialReport);
  const [justUnlocked, setJustUnlocked] = useState(false);
  const unlocked = Boolean(report);
  const ungated = Boolean(teaser.ungated);
  const bfcm = teaser.bfcm;

  /* The reader unlocks at the bottom of the page but the bundles render
     further up; carry them to what they just opened. Initial-load-unlocked
     visits (revisiting a claimed link) stay at the top. */
  useEffect(() => {
    if (justUnlocked) document.getElementById("your-bundles")?.scrollIntoView({ behavior: "smooth" });
  }, [justUnlocked]);

  return (
    <div className="space-y-16">
      <ScoreHero teaser={teaser} bfcm={bfcm} />
      {bfcm && <BfcmClock bfcm={bfcm} />}
      <GapBars gap={teaser.gap} currency={teaser.currencySymbol} bfcm={bfcm} />
      <FormatChecklist teaser={teaser} report={report} bfcm={bfcm} />
      {unlocked && ungated && <SaveResultsCta teaser={teaser} />}
      {unlocked && <BundlesSection bundles={report.bundles} demoPath={report.demoPath} storeName={teaser.storeName} />}
      {unlocked && ungated && <TeamSetupCta teaser={teaser} claimUrl={claimUrl} bfcm={bfcm} />}
      {bfcm && <BfcmPlaybook bfcm={bfcm} />}

      {unlocked ? (
        <ClosingCta closing={report.closing} storeName={teaser.storeName} claimUrl={claimUrl} supportUrl={supportUrl} bfcm={bfcm} />
      ) : (
        <UnlockGate teaser={teaser} onUnlocked={(next) => { setReport(next); setJustUnlocked(true); }} bfcm={bfcm} />
      )}
    </div>
  );
}

/* ---------- Above the gate ---------- */

function ScoreHero({ teaser, bfcm }) {
  return (
    <section className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
      <div>
        <p className="kicker mb-4" data-reveal>
          {bfcm ? `${bfcm.scoreName || "BFCM readiness score"} for ${teaser.storeName}` : `AOV audit for ${teaser.storeName}`} · {teaser.domain}
        </p>
        <h1 className="display max-w-2xl text-4xl sm:text-5xl" data-reveal data-reveal-delay="60">{teaser.headline}</h1>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft" data-reveal data-reveal-delay="140">{teaser.subheadline}</p>
        <div className="mt-7 flex flex-wrap gap-3 text-sm" data-reveal data-reveal-delay="200">
          <span className="rounded-full border border-ink/10 bg-white px-4 py-2 font-medium">{Number(teaser.productsCount || 0).toLocaleString()} products read</span>
          {teaser.collectionsCount > 0 && (
            <span className="rounded-full border border-ink/10 bg-white px-4 py-2 font-medium">{teaser.collectionsCount} collections</span>
          )}
          <span className="rounded-full border border-ink/10 bg-white px-4 py-2 font-medium">
            {moneyRange(teaser.currencySymbol, teaser.gap?.gap_low, teaser.gap?.gap_high)} uncaptured per order
          </span>
        </div>
      </div>
      <div className="card p-8 text-center" data-reveal data-reveal-delay="120">
        <ScoreGauge score={teaser.score} grade={teaser.grade} />
        <p className="mt-3 font-semibold">{bfcm ? "Bundles readiness score" : "Merchandising score"}</p>
        {bfcm?.band && (
          <p className="mt-1.5 inline-block rounded-full bg-sky-wash px-3.5 py-1 text-sm font-bold text-sky-deep">{bfcm.band}</p>
        )}
        <p className="mt-1 text-sm text-ink-soft">Scored on bundle formats, upsells, merchandising depth and pricing, all from your public catalog.</p>
      </div>
    </section>
  );
}

function ScoreGauge({ score, grade }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setProgress(score || 0), 300);
    return () => clearTimeout(t);
  }, [score]);

  const r = 62;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(progress, 100) / 100);

  return (
    <div className="relative mx-auto h-44 w-44" role="img" aria-label={`Merchandising score ${score} out of 100, grade ${grade}`}>
      <svg viewBox="0 0 160 160" className="h-full w-full">
        <circle cx="80" cy="80" r={r} fill="none" stroke="var(--color-sky-wash)" strokeWidth="12" />
        <circle
          cx="80" cy="80" r={r} fill="none"
          stroke="var(--color-sky-deep)" strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          transform="rotate(-90 80 80)"
          style={{ transition: "stroke-dashoffset 1.3s cubic-bezier(0.22, 1, 0.36, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="display text-5xl">{grade}</span>
        <span className="mt-1 text-sm text-ink-soft">{score} / 100</span>
      </div>
    </div>
  );
}

/* The seasonal clock: how much runway is left and why it matters. The server
   writes the copy and the countdown (bfcm.clock); dark on purpose, the one
   urgent beat on the page. */
function BfcmClock({ bfcm }) {
  const clock = bfcm.clock || {};
  return (
    <section className="rounded-[1.25rem] bg-ink p-8 text-white shadow-lift sm:p-10">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
        <div>
          <p className="kicker mb-3 !text-sky">{clock.kicker}</p>
          <h2 className="display text-3xl sm:text-4xl">{clock.headline}</h2>
          <p className="mt-3 max-w-2xl leading-relaxed text-white/70">{clock.body}</p>
        </div>
        {clock.statValue != null && (
          <div className="shrink-0 rounded-2xl bg-white/10 px-7 py-5 text-center">
            <div className="display text-5xl">{clock.statValue}</div>
            <div className="mt-1 text-sm text-white/70">{clock.statLabel}</div>
          </div>
        )}
      </div>
    </section>
  );
}

function GapBars({ gap, currency, bfcm }) {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGrown(true), 350);
    return () => clearTimeout(t);
  }, []);
  if (!gap) return null;

  const max = gap.bundled_high || 1;
  const rows = [
    { label: "A typical order today", low: gap.order_value_low, high: gap.order_value_high, color: "#4a5158" },
    { label: "The same order with bundles", low: gap.bundled_low, high: gap.bundled_high, color: "var(--color-sky-deep)" },
  ];

  return (
    <section className="card p-8 sm:p-10" data-reveal>
      <p className="kicker mb-3">The gap, per order</p>
      <h2 className="display max-w-xl text-3xl">{bfcm ? `Your profit per session could be way higher ${bfcm.seasonLabel || "this BFCM"}.` : `Every order is ${gap.lift_percent}% smaller than it could be.`}</h2>
      <div className="mt-8 space-y-5">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="mb-1.5 flex items-baseline justify-between gap-4 text-sm">
              <span className="font-semibold">{row.label}</span>
              <span className="font-display text-lg font-bold">{moneyRange(currency, row.low, row.high)}</span>
            </div>
            <div className="h-3.5 overflow-hidden rounded-md bg-ink/5">
              <div
                className="h-full rounded-md"
                style={{
                  width: grown ? `${(row.high / max) * 100}%` : "0%",
                  background: row.color,
                  transition: "width 1.1s cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <Methodology gap={gap} currency={currency} />
    </section>
  );
}

/* Answers "where do these numbers come from?" in plain sight, before the
   gate. Every claim on the page traces to one of these four sources. */
function Methodology({ gap, currency }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-ink/10 bg-paper p-5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left text-sm font-semibold"
        aria-expanded={open}
      >
        Where these numbers come from
        <span aria-hidden="true" className={`transition-transform ${open ? "rotate-180" : ""}`}>⌄</span>
      </button>
      {open && (
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ink-soft">
          <li>Your catalog: read straight from your public storefront (products and collections). No API keys, no app access, no private data.</li>
          <li>Your typical order value: we can't see your real orders, so we proxy it with your median to average product price, {moneyRange(currency, gap.order_value_low, gap.order_value_high)}. That's why it's shown as a range, not a fake-precise number.</li>
          <li>The {gap.lift_percent}% lift: the published average across 25,000+ merchants using Simple Bundles. It's the only benchmark we apply, and we apply it to your own prices.</li>
          <li>The upsell check: we read the apps actually running on your storefront's homepage.</li>
        </ul>
      )}
    </div>
  );
}

function FormatChecklist({ teaser, report, bfcm }) {
  const findings = report?.formatFindings;
  const statuses = findings || teaser.formatStatuses || [];

  return (
    <section data-reveal>
      <p className="kicker mb-3">{bfcm ? seasonSkin(bfcm).leakKicker : "Where the money leaks"}</p>
      <h2 className="display max-w-2xl text-3xl sm:text-4xl">
        {bfcm
          ? seasonSkin(bfcm).leakHeadline
          : "Four ways stores lift order value. Here's your coverage."}
      </h2>
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {statuses.map((finding) => {
          const chip = STATUS_CHIPS[finding.status] || STATUS_CHIPS.missing;
          return (
            <div key={finding.key} className="card p-6">
              <div className="flex items-center justify-between gap-3">
                <h3 className="display text-xl">{FORMAT_LABELS[finding.key] || finding.key}</h3>
                <span className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${chip.classes}`}>
                  <span aria-hidden="true">{chip.icon}</span> {chip.label}
                </span>
              </div>
              {finding.insight ? (
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">{finding.insight}</p>
              ) : (
                <p className="mt-3 text-sm italic text-ink-soft/70">Unlock your bundles below for the full read on this.</p>
              )}
              {bfcm && seasonSkin(bfcm).tips[finding.key] && (
                <p className="mt-3 rounded-xl bg-sky-wash px-4 py-3 text-sm leading-relaxed text-sky-deep">
                  <span className="font-bold">{seasonSkin(bfcm).tipLabel}</span>{seasonSkin(bfcm).tips[finding.key]}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* Free value above the gate: the playbook every store gets, no email needed.
   The personalized half (their six bundles) stays behind the unlock. */
function BfcmPlaybook({ bfcm }) {
  const skin = seasonSkin(bfcm);
  return (
    <section data-reveal>
      <p className="kicker mb-3">{skin.playbookKicker}</p>
      <h2 className="display max-w-2xl text-3xl sm:text-4xl">Six moves the ready stores make.</h2>
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {skin.playbook.map((tip, index) => (
          <div key={tip.title} className="card p-7">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-sky-wash font-display text-lg font-bold text-sky-deep">{index + 1}</div>
            <h3 className="display text-xl">{tip.title}</h3>
            <p className="mt-2.5 text-[0.95rem] leading-relaxed text-ink-soft">{tip.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- The ungated CTAs ---------- */

/* Both CTAs speak to the same unlock endpoint the gate used; the intent
   tells the server which mail to send (the results link to the visitor,
   or a heads-up to our shared inbox). The page never locks either way. */
function useEmailCapture(teaser, intent) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sentTo, setSentTo] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(teaser.unlockPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ email, intent }),
      });
      const data = await response.json();
      if (response.ok) {
        trackEvent("generate_lead", { form: `bfcm_readiness_${intent}` });
        setSentTo(email);
      } else {
        setError(Object.values(data.errors || {}).flat().join(" ") || "Something went wrong. Please try again.");
      }
    } catch {
      setError("We couldn't reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return { email, setEmail, submitting, error, sentTo, submit };
}

const CTA_INPUT_CLASSES =
  "w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink outline-none transition placeholder:text-ink/30 focus:border-sky-deep focus:ring-4 focus:ring-sky-wash";

/* CTA one, right before the bundles: mail yourself the link. No gate, no
   pressure; everything below stays visible either way. */
function SaveResultsCta({ teaser }) {
  const capture = useEmailCapture(teaser, "email_results");

  return (
    <section className="card p-8 sm:p-10" data-reveal>
      <div className="flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="kicker mb-3">Keep this page</p>
          <h2 className="display text-2xl sm:text-3xl">Send these results to your inbox.</h2>
          <p className="mt-2.5 max-w-md text-[0.95rem] leading-relaxed text-ink-soft">
            Your bundles are just below, no email needed. Send yourself the link
            and come back to them later, with your whole team.
          </p>
        </div>
        {capture.sentTo ? (
          <p className="flex items-center gap-2.5 rounded-xl bg-mint px-5 py-4 text-sm font-semibold lg:max-w-xs">
            <span aria-hidden="true">✓</span> Sent. Your results link is on its way to {capture.sentTo}.
          </p>
        ) : (
          <form onSubmit={capture.submit} className="w-full lg:max-w-md" noValidate>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                required
                placeholder="you@brand.com"
                aria-label="Work email"
                value={capture.email}
                onChange={(e) => capture.setEmail(e.target.value)}
                className={CTA_INPUT_CLASSES}
              />
              <button type="submit" className="btn-primary shrink-0" disabled={capture.submitting}>
                {capture.submitting ? "Sending…" : "Email me my results"}
              </button>
            </div>
            {capture.error && <p className="mt-2 text-sm text-red-600">{capture.error}</p>}
          </form>
        )}
      </div>
    </section>
  );
}

/* CTA two, right after the bundles: our team gets the lineup live with you,
   or take the self-serve door to the app listing. */
function TeamSetupCta({ teaser, claimUrl, bfcm }) {
  const skin = seasonSkin(bfcm);
  const capture = useEmailCapture(teaser, "team_setup");

  return (
    <section className="mx-auto max-w-2xl" data-reveal>
      <div className="card p-8 text-center shadow-float sm:p-12">
        <p className="kicker mb-3">{skin.readyKicker}</p>
        <h2 className="display text-3xl sm:text-4xl">
          We already built your {skin.lineupNoun}, now let us help you put it into action.
        </h2>
        <p className="mx-auto mt-4 max-w-lg text-[0.95rem] leading-relaxed text-ink-soft">{skin.teamSetupBody}</p>
        {capture.sentTo ? (
          <p className="mx-auto mt-7 flex max-w-lg items-center justify-center gap-2.5 rounded-xl bg-mint px-5 py-4 text-sm font-semibold">
            <span aria-hidden="true">✓</span> Got it. Our team will reach out at {capture.sentTo} to get these live.
          </p>
        ) : (
          <form onSubmit={capture.submit} className="mx-auto mt-7 max-w-lg" noValidate>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                required
                placeholder="Work email"
                aria-label="Work email"
                value={capture.email}
                onChange={(e) => capture.setEmail(e.target.value)}
                className={CTA_INPUT_CLASSES}
              />
              <button type="submit" className="btn-primary shrink-0" disabled={capture.submitting}>
                {capture.submitting ? "Sending…" : "Claim my bundles"}
                {!capture.submitting && <span aria-hidden="true">→</span>}
              </button>
            </div>
            {capture.error && <p className="mt-2 text-sm text-red-600">{capture.error}</p>}
          </form>
        )}
        <div className="mx-auto mt-8 flex max-w-lg items-center gap-4 text-xs font-bold tracking-widest text-ink-soft/60" aria-hidden="true">
          <span className="h-px flex-1 bg-ink/10" />OR<span className="h-px flex-1 bg-ink/10" />
        </div>
        <a href={claimUrl} target="_blank" rel="noopener" className="btn-ghost mt-7">
          Get started on my own <span aria-hidden="true">→</span>
        </a>
      </div>
    </section>
  );
}

/* ---------- The gate ---------- */

function UnlockGate({ teaser, onUnlocked, bfcm }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(teaser.unlockPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        trackEvent("generate_lead", { form: bfcm ? "bfcm_readiness_unlock" : "aov_audit_unlock" });
        onUnlocked(data.report);
      } else {
        setError(Object.values(data.errors || {}).flat().join(" ") || "Something went wrong. Please try again.");
        setSubmitting(false);
      }
    } catch {
      setError("We couldn't reach the server. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <section className="relative" data-reveal>
      {/* A blurred stand-in for the report below the gate; the real content
          never reaches the browser until the unlock round-trip. */}
      <div className="pointer-events-none select-none space-y-6 blur-sm" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="card p-8">
            <div className="shimmer-line mb-4 h-4 w-40" />
            <div className="shimmer-line mb-2.5 h-3.5 w-full" />
            <div className="shimmer-line mb-2.5 h-3.5 w-11/12" />
            <div className="shimmer-line h-3.5 w-2/3" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-paper/20 via-paper/80 to-paper" />

      <div className="absolute inset-x-0 top-10 mx-auto max-w-lg px-5">
        <div className="card p-8 shadow-float sm:p-10">
          <p className="kicker mb-3">{bfcm ? seasonSkin(bfcm).readyKicker : "Your bundles are ready"}</p>
          <h2 className="display text-3xl">
            {bfcm
              ? `We already built ${teaser.storeName}'s ${seasonSkin(bfcm).lineupNoun}.`
              : `We already built the fix for ${teaser.storeName}.`}
          </h2>
          <ul className="mt-5 space-y-2.5 text-[0.95rem] text-ink-soft">
            {(bfcm
              ? [
                  seasonSkin(bfcm).gateBullet,
                  "Every widget is live and interactive: gift sets, mix and match, build-your-own",
                  "The full read on each gap in your readiness score",
                ]
              : [
                  "Six ready-made bundles, built from your real products",
                  "Every widget is live and interactive — kits, mix & match, build-your-own",
                  "The full read on each format your store is missing",
                ]
            ).map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-mint text-[11px] font-bold">✓</span>
                {line}
              </li>
            ))}
          </ul>
          <form onSubmit={submit} className="mt-7" noValidate>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold">Work email</span>
              <input
                type="email"
                required
                placeholder="you@brand.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink outline-none transition placeholder:text-ink/30 focus:border-sky-deep focus:ring-4 focus:ring-sky-wash"
              />
            </label>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <button type="submit" className="btn-primary mt-4 w-full text-base" disabled={submitting}>
              {submitting ? "Unlocking…" : bfcm ? seasonSkin(bfcm).unlockCta : "Show me my bundles"}
              {!submitting && <span aria-hidden="true">→</span>}
            </button>
          </form>
          <p className="mt-4 text-center text-xs text-ink-soft">Free · This link stays unlocked for your whole team</p>
        </div>
      </div>

      {/* Reserve height for the overlaid card on small screens */}
      <div className="invisible h-[26rem] sm:h-80" aria-hidden="true" />
    </section>
  );
}

/* ---------- Behind the gate ---------- */

/* Post-unlock, the bundles ARE the report: six interactive widgets, rendered
   above the playbook, then one closing CTA at the foot of the page — no
   essay in between. The narrator still writes the long-form findings
   (verdict, market basket, gold standard, quick wins); they're just no
   longer rendered here. */
function BundlesSection({ bundles, demoPath, storeName }) {
  if (!bundles?.length) return null;
  return (
    <section id="your-bundles" data-reveal>
      <p className="kicker mb-3">We already built the fix</p>
      <h2 className="display max-w-2xl text-3xl sm:text-4xl">Six bundles, designed from your catalog.</h2>
      <p className="mt-4 max-w-2xl text-lg text-ink-soft">
        These aren't mockups. Every kit, set and builder below was designed from {storeName}'s
        real products. Every widget is interactive. Go ahead, click things.
      </p>
      <div className="mt-12">
        <DemoShowcase bundles={bundles} />
      </div>
      {demoPath && (
        <p className="mt-10 text-center">
          <a href={demoPath} target="_blank" rel="noopener" className="btn-ghost">Open your full demo page →</a>
        </p>
      )}
    </section>
  );
}

function ClosingCta({ closing, storeName, claimUrl, supportUrl, bfcm }) {
  return (
    <section className="card overflow-hidden bg-gradient-to-br from-sky-wash via-white to-sand p-12 text-center sm:p-16" data-reveal>
      <p className="kicker mb-4">{bfcm ? seasonSkin(bfcm).ctaKicker : "The fix is ready"}</p>
      <h2 className="display mx-auto max-w-2xl text-3xl sm:text-5xl">
        {bfcm
          ? seasonSkin(bfcm).ctaHeadline(storeName)
          : `These could be live on ${storeName} this week.`}
      </h2>
      {bfcm?.earlyBirdOffer && (
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-ink-soft">{bfcm.earlyBirdOffer}</p>
      )}
      {closing && <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-ink-soft">{closing}</p>}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
        <a href={claimUrl} target="_blank" rel="noopener" className="btn-primary text-base">Claim my bundles</a>
        <a href={supportUrl} target="_blank" rel="noopener" className="btn-ghost text-base">Talk to our team</a>
      </div>
      <p className="mt-6 text-sm text-ink-soft">4.9★ on the Shopify App Store · Trusted by 25,000+ merchants · 20% more AOV is the typical result</p>
    </section>
  );
}
